import { err, ok } from 'neverthrow';
import type { TokenMeta, WithMeta, WithRange } from '../../lex';
import * as EraQLAST from '../../eraql-ast/ast';
import type {
  ParseRes,
  ParseCtx,
  EraQlAst,
  ParseError,
} from '../../eraql-ast/ast';
import * as EraTy from '../../../ty/ty';
import { Expression } from '../../../builder/expression';
import { Relation } from '../../../builder/relation';
import {
  compileAst,
  compileToExpr,
  expectMu,
  expectOrderBy,
  expectRel,
  expectTy,
} from '../../eraql-ast/compile';
import { IdentToken } from '../../lex/tokens';
import { AST } from '../../../ast';
import { Markup } from '../../../builder';
import { Assert } from '@cotera/utilities';
import type { Scalar as ScalarTy } from '../../../ty/ty';
import { TypeDoesNotMatchExpectation } from '../../../type-checker/type-check-error';
import { TyStackTrace } from '../../../type-checker';
export type ParseFunc<T> = (
  ast: WithMeta<EraQlAst | EraQLAST.Item>,
  ctx: ParseCtx
) => ParseRes<T>;

export const IncludeMeta =
  <T>(func: ParseFunc<T>): ParseFunc<WithMeta<T>> =>
  (ast, ctx) =>
    func(ast, ctx).map((res) => [res, ast[1]] as const);

export const Ident: ParseFunc<IdentToken> = (
  [arg, meta],
  _ctx: ParseCtx
): ParseRes<IdentToken> => {
  if (arg.t !== 'ident') {
    return err([[{ t: 'expected', expected: 'ident' }, meta]]);
  }
  return ok(arg);
};

export const BoolLit: ParseFunc<boolean> = ([arg, argMeta], ctx) =>
  ConstExpr([arg, argMeta], ctx).andThen(
    (x): ParseRes<boolean> =>
      typeof x === 'boolean'
        ? ok(x)
        : err([[{ t: 'expected', expected: 'boolean-lit' }, argMeta]])
  );

export const EnumLit =
  <K extends string>(...keys: K[]): ParseFunc<K> =>
  (ast, ctx): ParseRes<K> =>
    StringLit(ast, ctx).andThen(
      (val): ParseRes<K> =>
        keys.includes(val as any)
          ? (ok(val) as ParseRes<K>)
          : err([
              [
                {
                  t: 'expected',
                  expected: `One of [${keys.map((k) => `"${k}"`).join(', ')}]`,
                },
                ast[1],
              ],
            ])
    );

export const ConstExpr: ParseFunc<ScalarTy> = ([ast, meta], ctx) =>
  Expr([ast, meta], ctx).andThen((expr): ParseRes<ScalarTy> => {
    const val = expr.tryEvaluate({
      allow: { now: false, rand: false, undefaultedVars: false },
    });

    if (val.isErr()) {
      return err([[{ t: 'expected', expected: 'constant' }, meta]]);
    }
    return ok(val.value);
  });

export const StringLit: ParseFunc<string> = (
  [arg, argMeta],
  ctx
): ParseRes<string> =>
  ConstExpr([arg, argMeta], ctx).andThen(
    (x): ParseRes<string> =>
      typeof x === 'string'
        ? ok(x)
        : err([[{ t: 'expected', expected: 'string-lit' }, argMeta]])
  );

export const NumLit: ParseFunc<number> = (
  [arg, argMeta],
  ctx: ParseCtx
): ParseRes<number> =>
  ConstExpr([arg, argMeta], ctx).andThen(
    (x): ParseRes<number> =>
      typeof x === 'number'
        ? ok(x)
        : err([[{ t: 'expected', expected: 'number-lit' }, argMeta]])
  );

export type KV<V> = { readonly k: TokenMeta; readonly v: WithMeta<V> };

type StructOfRes<Required, Optional> = {
  [Key in keyof Required]: Required[Key] extends ParseFunc<infer Inner>
    ? KV<Inner>
    : never;
} & {
  [Key in keyof Optional]?: Optional[Key] extends ParseFunc<infer Inner>
    ? KV<Inner>
    : never;
};

export const StructOf =
  <
    Required extends Record<string, ParseFunc<unknown>>,
    Optional extends Record<string, ParseFunc<unknown>>
  >(config: {
    required: Required;
    optional: Optional;
    // This is kinda a last resort, if we can avoid it lets make things as
    // strict as long as possibe before changing this to `boolean`
    strict: true;
  }): ParseFunc<StructOfRes<Required, Optional>> =>
  (ast, ctx) => {
    const { required, optional, strict } = config;

    const struct = AnyStruct(ast, ctx);

    if (struct.isErr()) {
      return err(struct.error);
    }

    const res: Record<string, KV<unknown>> = {};
    const errors: WithRange<ParseError>[] = [];

    const reqs: { key: string; func: ParseFunc<unknown>; required: boolean }[] =
      [];

    for (const [key, func] of Object.entries(required)) {
      reqs.push({ key, func, required: true });
    }

    for (const [key, func] of Object.entries(optional)) {
      reqs.push({ key, func, required: false });
    }

    for (const { key, func, required } of reqs) {
      const provided = struct.value[key];
      if (provided === undefined) {
        if (required) {
          errors.push([{ t: 'missing-key', key }, ast[1]]);
        }
        continue;
      }

      const valRes = func(provided.v, ctx);

      if (valRes.isErr()) {
        errors.push(...valRes.error);
      } else {
        const kv: KV<unknown> = {
          k: provided.k,
          v: [valRes.value, provided.v[1]],
        };
        res[key] = kv;
      }
    }

    if (strict) {
      for (const [key, val] of Object.entries(struct.value)) {
        if (!(key in optional) && !(key in required)) {
          errors.push([{ t: 'unexpected-key', key }, val.k]);
        }
      }
    }

    return errors.length > 0
      ? err(errors)
      : ok(res as StructOfRes<Required, Optional>);
  };

export const AnyStruct: ParseFunc<
  Record<string, KV<EraQlAst | EraQLAST.Item>>
> = (
  [ast, meta],
  _ctx: ParseCtx
): ParseRes<Record<string, KV<EraQlAst | EraQLAST.Item>>> => {
  if (ast.t !== 'assoc') {
    return err([[{ t: 'expected', expected: 'struct' }, meta]]);
  }

  // TODO: implmenet
  if (ast.entries.some(([entry, _]) => entry.t === 'assoc-spread')) {
    return err([
      [{ t: 'custom', msg: 'spread operator not yet supported' }, meta],
    ]);
  }

  const kvs: [string, KV<EraQlAst>][] = ast.entries.map<[string, KV<EraQlAst>]>(
    ([entry, _meta]) => {
      Assert.assert(entry.t === 'assoc-kv');
      return [entry.key[0], { k: entry.key[1], v: entry.val }];
    }
  );

  return ok(Object.fromEntries(kvs));
};

export const RecordOf =
  <Val>(of: ParseFunc<Val>): ParseFunc<Record<string, KV<Val>>> =>
  ([ast, astMeta], ctx) => {
    const result: Record<string, KV<Val>> = {};
    const errs: WithRange<ParseError>[] = [];

    if (ast.t !== 'assoc') {
      const res = compileAst([ast, astMeta], ctx);

      if (res.isErr()) {
        return err(res.error);
      }

      const { item } = res.value;

      switch (item.t) {
        case 'item-map': {
          for (const [k, v] of Object.entries(item.map)) {
            const vRes = of([v.item, astMeta], ctx);
            if (vRes.isErr()) {
              errs.push(...vRes.error);
            } else {
              result[k] = { k: astMeta, v: [vRes.value, astMeta] };
            }
          }
          break;
        }
        default:
          return err([[{ t: 'expected', expected: 'struct' }, astMeta]]);
      }
    } else {
      for (const [node, _nodeMeta] of ast.entries) {
        if (node.t === 'assoc-kv') {
          const valRes = of(node.val, ctx);

          if (valRes.isErr()) {
            if (ctx.eager) {
              return err(valRes.error);
            } else {
              errs.push(...valRes.error);
            }
          } else {
            result[node.key[0]] = {
              k: node.key[1],
              v: [valRes.value, node.val[1]],
            };
          }
        } else {
          const innerVals = RecordOf(of)(node.val, ctx);
          if (innerVals.isErr()) {
            errs.push(...innerVals.error);
          } else {
            for (const [k, v] of Object.entries(innerVals.value)) {
              result[k] = v;
            }
          }
        }
      }
    }

    return errs.length > 0 ? err(errs) : ok(result);
  };

export const OrderBy: ParseFunc<{
  expr: Expression;
  direction: AST.SortDirection;
}> = (ast, ctx) => expectOrderBy(ast, ctx);

export const ListOf =
  <T>(
    of: (ast: WithMeta<EraQlAst | EraQLAST.Item>, ctx: ParseCtx) => ParseRes<T>
  ) =>
  (
    ast: WithMeta<EraQlAst | EraQLAST.Item>,
    ctx: ParseCtx
  ): ParseRes<WithMeta<T>[]> => {
    const list = List(ast, ctx);

    if (list.isErr()) {
      return err(list.error);
    }

    const items: WithMeta<T>[] = [];
    const errs: WithRange<ParseError>[] = [];
    for (const [itemAst, itemMeta] of list.value) {
      const res = of([itemAst, itemMeta], ctx);
      if (res.isErr()) {
        if (ctx.eager) {
          return err(res.error);
        } else {
          errs.push(...res.error);
        }
      } else {
        items.push([res.value, itemMeta]);
      }
    }

    return errs.length > 0 ? err(errs) : ok(items);
  };

export const Closure =
  (opts: {
    arity: number[];
  }): ParseFunc<WithMeta<EraQLAST.ClosureWithBindings>> =>
  ([ast, meta], ctx) => {
    let output: WithMeta<EraQLAST.ClosureWithBindings>;
    if (ast.t === 'closure') {
      const item: EraQLAST.ClosureWithBindings = {
        t: 'closure-with-bindings',
        closure: ast,
        bindings: ctx.bindings ?? {},
      };

      output = [item, meta];
    } else {
      const res = compileAst([ast, meta], ctx);

      if (res.isErr()) {
        return err(res.error);
      }

      if (res.value.item.t !== 'closure-with-bindings') {
        const e: WithMeta<ParseError> = [
          { t: 'expected', expected: 'closure' },
          meta,
        ];
        return err([e]);
      }

      output = [res.value.item, meta];
    }

    if (!opts.arity.includes(output[0].closure.args[0].length)) {
      const e: WithMeta<ParseError> = [
        { t: 'invalid-arity', allowed: opts.arity },
        meta,
      ];
      return err([e]);
    }

    return ok(output);
  };

export const List: ParseFunc<readonly WithMeta<EraQlAst | EraQLAST.Item>[]> = (
  ast,
  ctx: ParseCtx
): ParseRes<readonly WithMeta<EraQlAst | EraQLAST.Item>[]> => {
  if (ast[0].t === 'list') {
    return ok(ast[0].elems);
  }

  const res = compileAst(ast, ctx).map((x) => x.item);
  if (res.isErr()) {
    return err(res.error);
  }
  switch (res.value.t) {
    case 'item-list':
      return ok(
        res.value.items.map((x): WithMeta<EraQLAST.Item> => [x.item, ast[1]])
      );
    default:
      return err([[{ t: 'expected', expected: 'list' }, ast[1]]]);
  }
};

export const EraqlAstOrItem: ParseFunc<
  WithMeta<EraQLAST.EraQlAst | EraQLAST.Item>
> = (ast, _ctx) => ok(ast);

export const Ty: ParseFunc<EraTy.ExtendedAttributeType> = (args, ctx) =>
  expectTy(args, ctx);
export const Expr: ParseFunc<Expression> = (args, ctx) =>
  compileToExpr(args, ctx);

export const Sp: ParseFunc<EraQLAST.SpecialFormItem> = (args, ctx) =>
  compileAst(args, ctx).andThen((res) => {
    if (res.item.t === 'special-form') {
      return ok(res.item);
    } else {
      const e: WithMeta<ParseError> = [
        { t: 'expected', expected: 'special-form' },
        args[1],
      ];
      return err([e]);
    }
  });

export const ExprOfTy =
  (ty: EraTy.Shorthand): ParseFunc<Expression> =>
  (args, ctx) =>
    Expr(args, ctx).andThen((expr): ParseRes<Expression> => {
      if (expr.implements(ty)) {
        return ok(expr);
      } else {
        const trace: TyStackTrace = new TypeDoesNotMatchExpectation({
          expected: ty,
          found: expr.ty,
        }).toStackTrace({});
        return err([[{ t: 'type-check-error', trace }, args[1]]]);
      }
    });

export const Rel: ParseFunc<Relation> = (args, ctx) => expectRel(args, ctx);

export const Mu: ParseFunc<Markup> = (args, ctx) => expectMu(args, ctx);

export const Scalar: ParseFunc<EraTy.Scalar> = (ast, ctx) => {
  const expr = compileToExpr(ast, ctx);
  if (expr.isErr()) {
    return err(expr.error);
  }
  const val = expr.value.tryEvaluate();

  if (val.isErr()) {
    throw new Error(
      `TODO INVALID EXPRESSION IN SCALAR EXPRESSION GOT (${expr.value.toEraQL()}) => ${JSON.stringify(
        val.error
      )}`
    );
  }

  return ok(val.value);
};

type ArgsOutput<T extends any[]> = {
  [Idx in keyof T]: T[Idx] extends ParseFunc<infer Output> ? Output : never;
};

export const ArgsWithOpts =
  <T extends ParseFunc<unknown>[], Opts extends ParseFunc<unknown>>(
    argsOf: T,
    optsOf?: Opts
  ) =>
  (
    args: readonly (WithMeta<EraQlAst | EraQLAST.Item> | undefined)[],
    ctx: ParseCtx,
    func: WithMeta<EraQlAst> | null
  ): ParseRes<
    [ArgsOutput<T>, opts?: Opts extends ParseFunc<infer O> ? O : never]
  > => {
    const errors: WithRange<ParseError>[] = [];
    const output: unknown[] = [];

    for (const [template, i] of argsOf.map((x, i) => [x, i] as const)) {
      const arg = args[i];
      if (arg === undefined) {
        errors.push([{ t: 'invalid-arity' }, func?.[1] ?? { range: null }]);
        break;
      } else {
        const res = template(arg, ctx);
        if (res.isOk()) {
          output.push(res.value);
        } else {
          errors.push(...res.error);
        }
      }
    }

    let opts: unknown | undefined = undefined;

    const optsArgs = args.at(argsOf.length);

    if (optsArgs && optsOf == undefined) {
      errors.push([{ t: 'invalid-arity' }, optsArgs[1]]);
      return err(errors);
    } else if (optsOf && optsArgs) {
      const res = optsOf(optsArgs, ctx);

      if (res.isOk()) {
        opts = res.value;
      } else {
        errors.push(...res.error);
      }
    }

    if (errors.length > 0) {
      return err(errors);
    } else {
      return ok(
        opts !== undefined
          ? [output as ArgsOutput<T>, opts as any]
          : [output as ArgsOutput<T>]
      );
    }
  };

export const ItemMap: ParseFunc<Record<string, EraQLAST.CompileOutput>> = (
  ast,
  ctx
) => {
  const res = compileAst(ast, ctx).map((x) => x.item);
  if (res.isErr()) {
    return err(res.error);
  }
  switch (res.value.t) {
    case 'item-map':
      return ok(res.value.map);
    default:
      return err([[{ t: 'expected', expected: 'map' }, ast[1]]]);
  }
};

export const Args =
  <T extends ParseFunc<unknown>[]>(...argsOf: T) =>
  (
    args: readonly (WithMeta<EraQlAst> | undefined)[],
    ctx: ParseCtx,
    func: WithMeta<EraQlAst> | null
  ): ParseRes<ArgsOutput<T>> =>
    ArgsWithOpts(argsOf)(args, ctx, func).map(([res]) => res);
